import { faFileSlash, faImageSlash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { themeClass } from '~/styles/theme.css'
import type { AttachmentType } from '../../types'
import {
  attachmentsGeneratingSubtitle,
  attachmentsGeneratingTitle,
  documentWrapper,
  noAttachments,
  noAttachmentsIcon
} from '../AttachmentsViewer.css'

export const PdfViewer = ({
  attachment,
  scale,
  onDocumentLoadSuccess
}: {
  attachment: AttachmentType
  scale: number
  onDocumentLoadSuccess: (info: { numPages: number }) => void
}) => {
  const [numPages, setNumPages] = useState<number>()
  const [previewWidth, setPreviewWidth] = useState<number>()
  const previewRef = useRef<HTMLDivElement>(null)
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null)

  const updatePreviewWidth = useCallback(() => {
    const wrapperWidth = previewRef.current?.clientWidth ?? 0
    setPreviewWidth(wrapperWidth - 30)
  }, [])

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    onDocumentLoadSuccess({ numPages })
    updatePreviewWidth()

    const currentRef = previewRef.current
    if (currentRef) {
      intersectionObserverRef.current?.disconnect()

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            updatePreviewWidth()
          }
        },
        { threshold: 0.1 }
      )
      observer.observe(currentRef)
      intersectionObserverRef.current = observer
    }
  }

  useEffect(() => {
    window.addEventListener('resize', updatePreviewWidth)
    // Pin viewer width changes when hash changes (e.g. when switching to/from CV tab)
    window.addEventListener('hashchange', updatePreviewWidth)
    return () => {
      window.removeEventListener('resize', updatePreviewWidth)
      window.removeEventListener('hashchange', updatePreviewWidth)
    }
  }, [updatePreviewWidth])

  useEffect(() => {
    return () => {
      intersectionObserverRef.current?.disconnect()
    }
  }, [])

  if (!attachment.content) {
    return (
      <div className={clsx(noAttachments, themeClass)}>
        <div className={noAttachmentsIcon}>
          <FontAwesomeIcon icon={faFileSlash} />
        </div>
        <div className={attachmentsGeneratingTitle}>
          Erreur lors du chargement du document
        </div>
        <div className={attachmentsGeneratingSubtitle}>
          Le contenu du document n'est pas disponible.
        </div>
      </div>
    )
  }

  return (
    <div ref={previewRef} className={documentWrapper}>
      <Document
        file={`data:application/pdf;base64,${attachment.content}`}
        onLoadSuccess={handleDocumentLoadSuccess}
        loading={<Shimmer height={400} />}
        error={
          <div className={noAttachments}>
            <div className={noAttachmentsIcon}>
              <FontAwesomeIcon icon={faImageSlash} />
            </div>
            Erreur lors du chargement du document
          </div>
        }
      >
        {Array.from(new Array(numPages), (_el, index) => (
          <Page
            key={`page_${
              // biome-ignore lint/suspicious/noArrayIndexKey: page can only be indexed by its number
              index + 1
            }`}
            pageNumber={index + 1}
            scale={scale}
            width={previewWidth}
          />
        ))}
      </Document>
    </div>
  )
}
